import { apiInstance, gql } from "@/services/httpAdapter";

// Interfaces / Types
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IRecalculateWashoutsParams, IUpdateCommissionCycleParams } from "@/Washouts/types/Api/commons";

// Helpers
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";
import { errorLogger } from "@/Common/helpers/logger";
import { isNil } from "@/Settings/helpers/SystemSettings/callActivity/utilities";

/**
 * Updates a commission cycle with dynamic return fields.
 *
 * @param params - Object containing locationId, commissionCycleId, input, and returnFields.
 * @returns The updated commission cycle with the requested fields.
 *
 * @example
 * const returnFields = `
 *   title
 *   status
 *   dataUpdatedAt
 *   payPeriods {
 *     _id
 *     title
 *     status
 *     positionStatus {
 *       position
 *       status
 *     }
 *   }
 * `;
 */
export async function updateCommissionCycle({ locationId, commissionCycleId, input, returnFields = "" }: IUpdateCommissionCycleParams) {
	const query = gql`
		mutation updateCommissionCycle(
			$locationId: String!
			$commissionCycleId: String!
			$input: UpdateCommissionCycleInput!
		) {
			updateCommissionCycle(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				input: $input
			) {
				_id
				${returnFields}
			}
		}
	`;

	const variables = { locationId, commissionCycleId, input };
	const response: GraphqlQueryResponse<"updateCommissionCycle", ICommissionCycle> = await apiInstance.post("/graphql", {
		query,
		variables,
	});
	return response.data.data?.updateCommissionCycle;
}

export async function recalculateWashout(params: IRecalculateWashoutsParams) {
	const { isDepartmental = false } = params;
	const query = gql`
		mutation recalculateWashout(
			$locationId: String!
			$position: String!
			$washoutId: String!
			$isDepartmental: Boolean
			$input: WashoutInput
		) {
			recalculateWashout(
				locationId: $locationId
				position: $position
				washoutId: $washoutId
				isDepartmental: $isDepartmental
				input: $input
			)
		}
	`;

	const sanitizedInput = { ...params.input };
	sanitizedInput.addedCompensations =
		sanitizedInput.addedCompensations?.map((row) => ({
			...row,
			gross: isNil(row.gross) ? "" : String(row.gross),
			tier: isNil(row.tier) ? "" : String(row.tier),
		})) ?? [];

	const variables = {
		...params,
		input: sanitizedInput,
		isDepartmental,
	};

	try {
		const response: GraphqlQueryResponse<"recalculateWashout", boolean> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.recalculateWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}
